import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { emailify, rutify } from '../apiUtils'

export enum UnidadMedidaTarifa {
  DIA = 'DIA',
  MMZ = 'TOQUES',
  ZPE = 'ZARPES',
  MES = 'MES',
}

export interface UnidadMedidaAplicada {
  dia: number
  mmz: number
  zpe: number
  mes: number
}

export enum TarifaEspecial {
  PILOTAJE = 0,
  AUXILIAR = 1,
  BUQUE_GIRA = 2,
}

export interface ITarifa {
  id: number
  descripcion: string
  codigo_sap: number
  valor: number
  unidad_medida: UnidadMedidaTarifa
  calculo: string
  activo: boolean
  fecha_creacion: string
  fecha_modificacion: string
}

export const defaultITarifa: ITarifa = {
  id: 0,
  descripcion: '',
  codigo_sap: 0,
  valor: 0,
  unidad_medida: UnidadMedidaTarifa.DIA,
  calculo: '',
  activo: true,
  fecha_creacion: new Date().toISOString(),
  fecha_modificacion: new Date().toISOString(),
}

export default class TarifasServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para cambios
    this.bucketID = dataStorageManager.registerBucket<ITarifa>(
      this.getAllTarifas.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.TARIFAS, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertTarifa',
            {
              operation: this.insertTarifa.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateTarifa',
            {
              operation: this.updateTarifa.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllTarifas(): Promise<ITarifa[]> {
    try {
      const response = await dataQueryManager.addQuery<ITarifa | ITarifa[]>({
        query: `${import.meta.env.VITE_BASE_URL}/mantendor/1/op_select_mantenedor_tarifas`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data.map((tarifa) => ({ ...tarifa, calculo: tarifa.calculo.toString() }))
      } else if (response.data) {
        return [{ ...response.data, calculo: response.data.calculo.toString() }]
      }
    } catch (error) {
      console.error('Error fetching cambios:', error)
    }
    return []
  }

  async insertTarifa(tarifaData: Omit<ITarifa, 'id' | 'fecha_creacion' | 'fecha_modificacion'>): Promise<boolean> {
    try {
      const body = {
        op_insert_mantenedor_tarifas: {
          descripcion: tarifaData.descripcion,
          codigo_sap: tarifaData.codigo_sap,
          valor: tarifaData.valor,
          unidad_medida: tarifaData.unidad_medida,
          calculo: tarifaData.calculo,
          activo: tarifaData.activo,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/mantendor/1/op_insert_mantenedor_tarifas`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error inserting cambios:', error)
    }
    return false
  }

  async updateTarifa(tarifaData: Omit<ITarifa, 'fecha_creacion' | 'fecha_modificacion'>): Promise<boolean> {
    try {
      // Telefono y email estan cambiados en api. Cambiar temporalmente hasta corregir en api
      const body = {
        op_update_mantenedor_tarifas: {
          descripcion: tarifaData.descripcion,
          codigo_sap: tarifaData.codigo_sap,
          valor: tarifaData.valor,
          unidad_medida: tarifaData.unidad_medida,
          calculo: tarifaData.calculo,
          activo: tarifaData.activo,
          id: tarifaData.id,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/mantendor/1/op_update_mantenedor_tarifas`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating cambios:', error)
    }
    return false
  }
}
